class CollectionFilterTabsSticky extends HTMLElement {
  #lastY = 0;
  #ticking = false;
  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;

    requestAnimationFrame(() => {
      const currentY = window.scrollY;
      const delta = currentY - this.#lastY;
      const threshold = this.offsetHeight;

      if (currentY <= threshold) {
        this.classList.remove('is-collapsed');
      } else if (delta > 4) {
        this.classList.add('is-collapsed');
      } else if (delta < -4) {
        this.classList.remove('is-collapsed');
      }

      this.#lastY = currentY;
      this.#ticking = false;
    });
  };

  connectedCallback() {
    this.#lastY = window.scrollY;
    window.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll);
  }
}

class CollectionTagFilter extends HTMLElement {
  #grid = null;
  #items = [];
  #emptyMessage = null;
  #tabs = [];
  #onPopState = () => {
    this.applyFilter(this.#getTagFromUrl(), { updateUrl: false });
  };

  connectedCallback() {
    if (this.#redirectLegacyTagUrl()) return;

    this.#grid = document.querySelector('[data-keeply-collection-grid]');
    if (!this.#grid) return;

    this.#items = [...this.#grid.querySelectorAll('[data-product-tags]')];
    this.#emptyMessage = this.#grid.querySelector('[data-filter-empty]');
    this.#tabs = [...this.querySelectorAll('[data-filter-tag]')];

    this.#tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        this.applyFilter(tab.dataset.filterTag || '');
      });
    });

    this.applyFilter(this.#getTagFromUrl(), { updateUrl: false });
    window.addEventListener('popstate', this.#onPopState);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.#onPopState);
  }

  #redirectLegacyTagUrl() {
    const collectionPath = this.dataset.collectionPath;
    if (!collectionPath) return false;

    const path = window.location.pathname;
    if (!path.startsWith(`${collectionPath}/`)) return false;

    const tagFromPath = path.slice(collectionPath.length + 1).split('/')[0];
    if (!tagFromPath) return false;

    window.location.replace(`${collectionPath}?tier=${encodeURIComponent(tagFromPath)}`);
    return true;
  }

  #getTagFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const queryTag = params.get('tier') || params.get('tag');
    if (queryTag) return queryTag.toLowerCase();

    const activeTab = this.#tabs.find((tab) => tab.classList.contains('is-active'));
    return (activeTab?.dataset.filterTag || '').toLowerCase();
  }

  applyFilter(tag, { updateUrl = true } = {}) {
    const normalizedTag = (tag || '').toLowerCase();

    this.#tabs.forEach((tab) => {
      const tabTag = (tab.dataset.filterTag || '').toLowerCase();
      const isActive = tabTag === normalizedTag;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    let visibleCount = 0;

    this.#items.forEach((item) => {
      const productTags = (item.dataset.productTags || '').split(/\s+/).filter(Boolean);
      const isVisible = !normalizedTag || productTags.includes(normalizedTag);
      item.hidden = !isVisible;
      if (isVisible) visibleCount += 1;
    });

    if (this.#emptyMessage) {
      this.#emptyMessage.hidden = visibleCount > 0;
    }

    if (updateUrl) {
      this.#updateUrl(normalizedTag);
    }
  }

  #updateUrl(tag) {
    const url = new URL(window.location.href);
    const collectionPath = this.dataset.collectionPath;

    if (collectionPath) {
      url.pathname = collectionPath;
    }

    url.searchParams.delete('tag');
    if (tag) {
      url.searchParams.set('tier', tag);
    } else {
      url.searchParams.delete('tier');
    }

    window.history.pushState({ collectionTagFilter: tag }, '', url);
  }
}

customElements.define('collection-filter-tabs-sticky', CollectionFilterTabsSticky);
customElements.define('collection-tag-filter', CollectionTagFilter);
